import React, { useRef, useState } from 'react';
import type { FormikProps } from 'formik';
import { withFormik, Form } from 'formik';
import { useDebounce } from 'react-use';

import round from 'lodash/round';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';

// Components
import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import TextField from 'src/components/ui/TextField';
import Select from 'src/components/ui/Select';
import Loader from 'src/components/ui/Loader';

// Hooks
import { useHomeLoanQuery } from 'src/hooks/__generated__/queries';
import theme from 'src/utils/theme';
import formatPrice from 'src/utils/formatPrice';
import { colours } from 'src/constants/colours';
import { styledComponent, styledTag } from 'src/utils/styled';
import { AnalyticsEvent } from 'src/utils/analytics';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

interface MonthlyStatsParam {
    color: SmallDotProps['$color'];
    label: string;
    data: string;
    targetId: string;
}

const ResultCard = styledComponent(Card)`
    padding: 1rem;
    background-color: ${theme.palette.primary.main};
    color: ${theme.palette.primary.contrastText};
`;
const BorderBox = styledComponent(Box)`
        border: 1px ${colours.black.O08} solid;
        border-radius: 1rem;
        padding: 1.5rem;
`;

ResultCard.defaultProps = {
    elevation: 0,
};

interface LinesProps {
    $gridTemplate: string;
}

const Lines = styledTag('div')`
    display: grid;
    grid-template-columns: ${(props: LinesProps) => props.$gridTemplate};
    gap: 0.0625rem;
    border-radius: 0.5rem;
    overflow: hidden;
    span {
        height: 1rem;
    }
    span:nth-of-type(1) {
        background-color: ${theme.palette.secondary.main};
    }
    span:nth-of-type(2) {
        background-color: ${theme.palette.tertiary.main};
    }
    span:nth-of-type(3) {
        background-color: ${theme.palette.primary.main};
    }
`;

export interface SmallDotProps {
    $color: 'primary' | 'secondary' | 'tertiary';
}

const SmallDot = styledTag('div')`
    width: 1rem;
    height: 1rem;
    border-radius: 100%;
    background-color: ${(props: SmallDotProps) => String(theme.palette[props.$color].main)};
    margin-right: 0.5rem;
`;

interface FormValues {
    housePrice: number;
    intakeAmount: number;
    interestRate: number;
    yearNumber: number;
}

const init = {
    housePrice: 500000,
    intakeAmount: 75000,
    interestRate: 4.5,
    yearNumber: 25,
};

const PageForm: React.FC<FormikProps<FormValues>> = ({ values, dirty }) => {
    const { t, i18n } = useTranslation(['edit-profile']);
    const { logEventDynamic } = useDynamicLogEvents();

    const [debouncedQuery, setDebouncedQuery] = useState(init);
    const eventHasBeenLogged = useRef<boolean>(false);
    useDebounce(
        () => {
            setDebouncedQuery(values);
            if (!eventHasBeenLogged.current && dirty) {
                logEventDynamic(AnalyticsEvent.LoanSimulation);
                eventHasBeenLogged.current = true;
            }
        },
        500,
        [values],
    );

    const { data } = useHomeLoanQuery({
        variables: {
            housePrice: Number(debouncedQuery.housePrice),
            intakeAmount: Number(debouncedQuery.intakeAmount),
            interestRate: Number(debouncedQuery.interestRate),
            yearNumber: Number(debouncedQuery.yearNumber),
        },
    });

    const linePartsRatios = data?.homeLoan
        ? [
              data.homeLoan.totalPrice > 0 ? data.homeLoan.totalLoanAmount / data.homeLoan.totalPrice : 0,
              data.homeLoan.totalPrice > 0 ? data.homeLoan.totalInterestAmount / data.homeLoan.totalPrice : 0,
              data.homeLoan.intakePercent,
          ]
        : [];

    const gridTemplate = linePartsRatios.map((ratio) => `${round(ratio * 100, 2)}fr`).join(' ');

    const yearsDropdownItems = [5, 10, 15, 20, 25].map((years) => ({
        value: years,
        label: t('edit-profile:years', { years }),
    }));

    const monthlyPaymentString =
        data?.homeLoan &&
        `${formatPrice({
            price: data.homeLoan.monthlyPayment,
            lang: i18n.language,
        })} / ${t('ad:month')}`;

    const monthlyStatsParams: MonthlyStatsParam[] | null | undefined = data?.homeLoan && [
        {
            color: 'secondary',
            label: t('edit-profile:totalLoanedAmount'),
            data: formatPrice({
                price: data.homeLoan.totalLoanAmount,
                lang: i18n.language,
            }),
            targetId: 'loan-simulator-total-loan',
        },
        {
            color: 'tertiary',
            label: t('edit-profile:totalInterests'),
            data: formatPrice({
                price: data.homeLoan.totalInterestAmount,
                lang: i18n.language,
            }),
            targetId: 'loan-simulator-total-interest',
        },
        {
            color: 'primary',
            label: t('edit-profile:intakePercent'),
            data: `${round(data.homeLoan.intakePercent * 100, 2)} %`,
            targetId: 'loan-simulator-intake-percent',
        },
    ];

    return (
        <Form id="simulator">
            <Card elevation={0}>
                <Grid spacing={2} pb={0} container>
                    <Grid xs={12} sm={6} mt={0} item>
                        <Box mt="-1rem" mb={0}>
                            <Box>
                                <TextField
                                    type="number"
                                    name="housePrice"
                                    label={t('edit-profile:housePrice')}
                                    InputProps={{
                                        inputProps: {
                                            'data-target-id': 'loan-simulator-home-price',
                                        },
                                    }}
                                    formatNumbers
                                />
                            </Box>
                            <Box mt="-1rem" mb={0}>
                                <TextField
                                    sx={{ mb: 0 }}
                                    type="number"
                                    name="intakeAmount"
                                    label={t('edit-profile:intakeAmount')}
                                    InputProps={{
                                        inputProps: {
                                            'data-target-id': 'loan-simulator-intake-amount',
                                        },
                                    }}
                                    formatNumbers
                                />
                            </Box>
                            <Box display="flex" flexDirection="column">
                                <Grid xs={6} sm={6} md={6} lg={6} xl={6} mt="-1rem" mb={0} sx={{}} item>
                                    <Select
                                        type="number"
                                        name="yearNumber"
                                        label={t('edit-profile:yearNumber')}
                                        items={yearsDropdownItems}
                                        InputProps={{
                                            inputProps: {
                                                'data-target-id': 'loan-simulator-years-number',
                                            },
                                        }}
                                        sx={{ height: '3rem' }}
                                    />
                                </Grid>
                                <Grid xs={6} sm={6} md={6} lg={6} xl={6} mt="-0.5rem" mb={0} item>
                                    <TextField
                                        type="number"
                                        name="interestRate"
                                        label={t('edit-profile:interestRate')}
                                        InputProps={{
                                            inputProps: {
                                                min: 0,
                                                max: 100,
                                                step: 0.01,
                                                'data-target-id': 'loan-simulator-interest-rate',
                                            },
                                        }}
                                    />
                                </Grid>
                            </Box>

                            <Box>
                                <Typography variant="body2">*{t('ad:simulationDisclaimer')}</Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid xs={12} sm={6} item>
                        <BorderBox>
                            {!data?.homeLoan ? (
                                <Loader />
                            ) : (
                                <>
                                    <Box mb={2}>
                                        <Typography variant="h5">{t('ad:monthlyPayments')}</Typography>
                                        <Typography
                                            variant="h1"
                                            color="primary"
                                            data-target-id="loan-simulator-monthly-payment"
                                            mt={0.5}
                                        >
                                            {monthlyPaymentString}
                                        </Typography>
                                    </Box>
                                    <Lines $gridTemplate={gridTemplate}>
                                        <span />
                                        <span />
                                        <span />
                                    </Lines>
                                    {monthlyStatsParams?.map((section, index) => (
                                        <Box
                                            key={section.label}
                                            mt={!index ? 2 : 0}
                                            mb={1}
                                            display="flex"
                                            alignItems="center"
                                        >
                                            <SmallDot $color={section.color} />
                                            <Typography variant="body1" color="dark">
                                                {section.label}
                                            </Typography>
                                            <Box ml={0.5}>
                                                <Typography
                                                    variant="body1"
                                                    color="dark"
                                                    weight="bold"
                                                    data-target-id={section.targetId}
                                                >
                                                    {section.data}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </BorderBox>
                    </Grid>
                </Grid>
            </Card>
        </Form>
    );
};

interface OuterProps {
    initialValues?: Partial<FormValues>;
}
const LoanSimulator = withFormik<OuterProps, FormValues>({
    mapPropsToValues: (props: OuterProps) => ({
        ...init,
        ...props.initialValues,
    }),
    handleSubmit: console.log,
})(PageForm);

export default LoanSimulator;
